import React, { ChangeEvent } from 'react';

import { Time } from '../../../../types/common.types';

import { ReactComponent as ChevronIcon } from '../../../../resources/icons/chevron.svg';

import './time-input.scss';

type Props = {
  value?: Time;
  onChange: (value: Time) => void;
  disabled?: boolean;
};

const TimeInput = ({ value = { hours: '', minutes: '', isAM: true }, onChange, disabled }: Props) => {
  const handleHoursChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: eventValue } = event.target;
    if (!isValidHoursInput(eventValue)) return;
    onChange({ ...value, hours: eventValue });
  };

  const handleMinutesChange = (event: ChangeEvent<HTMLInputElement>) => {
    const { value: eventValue } = event.target;
    if (!isValidMinutesInput(eventValue)) return;
    onChange({ ...value, minutes: eventValue });
  };

  const handleHoursBlur = () => {
    if (value.hours === '0') {
      onChange({ ...value, hours: '' });
    } else if (value.hours.length === 1) {
      onChange({ ...value, hours: padTime(value.hours) });
    }
  };

  const handleMinutesBlur = () => {
    if (value.minutes.length === 1) {
      onChange({ ...value, minutes: padTime(value.minutes) });
    }
  };

  return (
    <div className="time-input">
      <div className="time-input__input-container">
        <input
          className="time-input__input"
          name="hours"
          value={value.hours}
          onChange={handleHoursChange}
          onBlur={handleHoursBlur}
          maxLength={2}
          inputMode="numeric"
          disabled={disabled}
          data-testid="time-input-hours"
        />
        <Arrows
          onIncrement={() => onChange({ ...value, hours: stepHours(value.hours, 1) })}
          onDecrement={() => onChange({ ...value, hours: stepHours(value.hours, -1) })}
          disabled={disabled}
          testId="time-input-hours"
        />
      </div>
      <div className="time-input__separator">
        <span />
        <span />
      </div>
      <div className="time-input__input-container">
        <input
          className="time-input__input"
          name="minutes"
          value={value.minutes}
          onChange={handleMinutesChange}
          onBlur={handleMinutesBlur}
          maxLength={2}
          inputMode="numeric"
          disabled={disabled}
          data-testid="time-input-minutes"
        />
        <Arrows
          onIncrement={() => onChange({ ...value, minutes: stepMinutes(value.minutes, 1) })}
          onDecrement={() => onChange({ ...value, minutes: stepMinutes(value.minutes, -1) })}
          disabled={disabled}
          testId="time-input-minutes"
        />
      </div>
      <div className="time-input__meridiem-toggle">
        <p className="time-input__meridiem">{value.isAM ? 'AM' : 'PM'}</p>
        <Arrows
          onIncrement={() => onChange({ ...value, isAM: true })}
          onDecrement={() => onChange({ ...value, isAM: false })}
          disabled={disabled}
          incrementDisabled={value.isAM}
          decrementDisabled={!value.isAM}
          testId="time-input-meridiem"
        />
      </div>
    </div>
  );
};

type ArrowsProps = {
  onIncrement: () => void;
  onDecrement: () => void;
  disabled?: boolean;
  incrementDisabled?: boolean;
  decrementDisabled?: boolean;
  testId?: string;
};

const Arrows = ({ onIncrement, onDecrement, disabled, incrementDisabled, decrementDisabled, testId }: ArrowsProps) => {
  return (
    <div className="time-input__arrows">
      <button
        className="time-input__arrow"
        onClick={onIncrement}
        disabled={disabled || incrementDisabled}
        data-testid={testId ? `${testId}-increment` : undefined}
      >
        <ChevronIcon className="time-input__arrow-icon" />
      </button>
      <button
        className="time-input__arrow"
        onClick={onDecrement}
        disabled={disabled || decrementDisabled}
        data-testid={testId ? `${testId}-decrement` : undefined}
      >
        <ChevronIcon className="time-input__arrow-icon time-input__arrow-icon--down" />
      </button>
    </div>
  );
};

const isValidHoursInput = (value: string): boolean => {
  if (value === '') return true;
  if (!/^\d{1,2}$/.test(value)) return false;
  if (value.length === 1) return true;
  const number = Number(value);
  return number >= 1 && number <= 12;
};

const isValidMinutesInput = (value: string): boolean => {
  if (value === '') return true;
  if (!/^\d{1,2}$/.test(value)) return false;
  if (value.length === 1) return true;
  const number = Number(value);
  return number >= 0 && number <= 59;
};

const padTime = (value: string): string => value.padStart(2, '0');

const stepHours = (hours: string, step: 1 | -1): string => {
  const current = hours === '' ? (step === 1 ? 0 : 1) : Number(hours);
  const next = ((((current - 1 + step + 12) % 12) + 12) % 12) + 1;
  return padTime(next.toString());
};

const stepMinutes = (minutes: string, step: 1 | -1): string => {
  const current = minutes === '' ? (step === 1 ? -1 : 0) : Number(minutes);
  const next = (((current + step) % 60) + 60) % 60;
  return padTime(next.toString());
};

export default TimeInput;
